import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import StatisticsModule from '../components/StatisticsModule';
import MedicalTicketModal from '../components/MedicalTicketModal';
import TechnicalTicketModal from '../components/TechnicalTicketModal';
import { LuLayoutDashboard } from "react-icons/lu";

import { 
  Plus,
  Grid3x3,
  X,
  CheckCircle,
  Clock,
  Calendar,
  TrendingUp,
  User,
  ClipboardList
} from 'lucide-react';
import { GiHorseHead } from 'react-icons/gi';
import { PiFarm } from "react-icons/pi";
import { FaHandHoldingMedical, FaWrench } from "react-icons/fa6";
import { LuBellRing } from "react-icons/lu";
import { MdOutlineQueryStats } from "react-icons/md";
import { MdWhereToVote } from "react-icons/md";
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';


export default function TableauDeBordListe() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showMedicalModal, setShowMedicalModal] = useState(false);
  const [showTechnicalModal, setShowTechnicalModal] = useState(false);
  const [ticketToClose, setTicketToClose] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupération des paramètres de filtrage
  const filterHorseId = location.state?.horseId;
  const filterType = location.state?.filter;
  const filterZoneId = location.state?.zoneId;
  const filterZoneName = location.state?.zoneName;

  // Charger les tickets en temps réel avec filtrage
  useEffect(() => {
    let q;
    
    // Filtre pour les tickets médicaux par cheval
    if (filterHorseId && filterType === 'medical') {
      q = query(
        collection(db, 'tickets'),
        where('horseId', '==', filterHorseId),
        where('statut', '==', 'ouvert'),
        where('type', '==', 'medical')
      );
    }
    // NOUVEAU : Filtre pour les tickets techniques par zone
    else if (filterZoneId && filterType === 'technique') {
      q = query(
        collection(db, 'tickets'),
        where('zoneId', '==', filterZoneId),
        where('statut', '==', 'ouvert'),
        where('type', '==', 'technique')
      );
    }
    // Pas de filtre - afficher tous les tickets
    else {
      q = query(
        collection(db, 'tickets'),
        where('statut', '==', 'ouvert'),
        where('type', 'in', ['medical', 'technique'])
      );
    }

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const ticketsData = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.type === 'medical' || data.type === 'technique') {
            ticketsData.push({
              id: doc.id,
              ...data
            });
          }
        });
        // Trier par priorité puis par date
        ticketsData.sort((a, b) => {
          const priorityOrder = { 'haute': 0, 'moyenne': 1, 'basse': 2 };
          const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
          if (priorityDiff !== 0) return priorityDiff;
          
          const dateA = new Date(a.dateCreation || 0);
          const dateB = new Date(b.dateCreation || 0);
          return dateB - dateA;
        });
        setTickets(ticketsData);
        setLoading(false);
      },
      (error) => {
        console.error('Erreur chargement tickets:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [filterHorseId, filterType, filterZoneId]);

  // Modal de confirmation de clôture - SORTI du useEffect
  const CloseConfirmModal = () => {
    if (!showCloseConfirm || !ticketToClose) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Confirmer la clôture</h3>
            <button
              onClick={() => {
                setShowCloseConfirm(false);
                setTicketToClose(null);
              }}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <p className="text-gray-600 mb-6">
            Êtes-vous sûr de vouloir clôturer ce ticket ? Cette action marquera le ticket comme résolu.
          </p>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowCloseConfirm(false);
                setTicketToClose(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={() => closeTicket(ticketToClose)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Confirmer la clôture
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Fermer un ticket
  const closeTicket = async (ticketId) => {
    try {
      const ticketRef = doc(db, 'tickets', ticketId);
      const now = new Date().toISOString();
      
      await updateDoc(ticketRef, {
        statut: 'cloture',
        dateCloture: now,
        historique: arrayUnion({
          date: now,
          acteur: 'Utilisateur',
          action: 'cloture'
        })
      });
      
      // Fermer les modals après succès
      setShowCloseConfirm(false);
      setTicketToClose(null);
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(null);
      }
    } catch (error) {
      console.error('Erreur fermeture ticket:', error);
      alert('Erreur lors de la fermeture du ticket');
    }
  };

  // Demander confirmation avant de fermer
  const handleCloseTicket = (ticketId, e) => {
    e?.stopPropagation();
    setTicketToClose(ticketId);
    setShowCloseConfirm(true);
  };

  // Formater la date et l'heure
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit',
      year: '2-digit'
    }) + ' ' + date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculer le temps écoulé depuis la création
  const getTimeSinceCreation = (dateString) => {
    if (!dateString) return '';
    
    const now = new Date();
    const created = new Date(dateString);
    const diffMs = now - created;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays}j ${diffHours}h`;
    } else {
      return `${diffHours}h`;
    }
  };

  // Tronquer le texte
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Obtenir le label de la sous-rubrique
  const getSubTypeLabel = (subType) => {
    const labels = {
      'infrastructure': 'Infrastructure',
      'eau_alimentation': 'Eau & Alimentation',
      'equipements': 'Équipements',
      'terrain': 'Terrain',
      'autres': 'Autres'
    };
    return labels[subType] || subType;
  };

  // Obtenir l'icône selon le type
  const getIcon = (type) => {
    switch(type) {
      case 'medical':
        return <FaHandHoldingMedical className="h-4 w-4" />;
      case 'technique':
        return <FaWrench className="h-4 w-4" />;
      default:
        return <LuLayoutDashboard className="h-4 w-4" />;
    }
  };

  // Filtrer les tickets par type
  const medicalTickets = tickets.filter(t => t.type === 'medical');
  const technicalTickets = tickets.filter(t => t.type === 'technique');

  // Données fictives pour échéances
  const mockDeadlines = [
    { id: 1, type: 'Vaccination', animal: 'Thunder', date: '15/12/2024', daysLeft: 5, priority: 'haute' },
    { id: 2, type: 'Vermifuge', animal: 'Eclipse', date: '20/12/2024', daysLeft: 10, priority: 'moyenne' },
    { id: 3, type: 'Maréchalerie', animal: 'Storm', date: '25/12/2024', daysLeft: 15, priority: 'basse' },
    { id: 4, type: 'Dentiste', animal: 'Luna', date: '02/01/2025', daysLeft: 23, priority: 'basse' }
  ];

  // Données fictives pour stats
  const mockStatsTable = [
    { label: 'Tickets résolus aujourd\'hui', value: 3 },
    { label: 'Tickets résolus cette semaine', value: 12 },
    { label: 'Tickets résolus ce mois', value: 28 },
    { label: 'Temps moyen de résolution', value: '4.5h' },
    { label: 'Taux de résolution', value: '92%' }
  ];

  // Composant pour la pastille de priorité
  const PriorityDot = ({ priority }) => {
    const colors = {
      haute: 'bg-red-500',
      moyenne: 'bg-orange-500',
      basse: 'bg-green-500'
    };
    return <div className={`w-3 h-3 rounded-full ${colors[priority] || 'bg-gray-400'}`} />;
  };

  // Modal de sélection du type de ticket
  const TicketTypeSelectionModal = () => {
    if (!showNewTicketModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h2 className="text-xl font-semibold mb-4">Nouveau ticket</h2>
          <p className="text-gray-600 mb-6">Quel type de ticket souhaitez-vous créer ?</p>
          
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => {
                setShowNewTicketModal(false);
                setShowMedicalModal(true);
              }}
              className="p-6 bg-blue-50 hover:bg-blue-100 border-2 border-blue-300 rounded-lg transition-all flex flex-col items-center gap-3"
            >
              <FaHandHoldingMedical className="h-8 w-8 text-blue-600" />
              <div>
                <div className="font-semibold text-blue-900">Médical</div>
                <p className="text-xs text-blue-700 mt-1">Soins vétérinaires</p>
              </div>
            </button>
            
            <button
              onClick={() => {
                setShowNewTicketModal(false);
                setShowTechnicalModal(true);
              }}
              className="p-6 bg-purple-50 hover:bg-purple-100 border-2 border-purple-300 rounded-lg transition-all flex flex-col items-center gap-3"
            >
              <FaWrench className="h-8 w-8 text-purple-600" />
              <div>
                <div className="font-semibold text-purple-900">Technique</div>
                <p className="text-xs text-purple-700 mt-1">Maintenance</p>
              </div>
            </button>
          </div>
          
          <button
            onClick={() => setShowNewTicketModal(false)}
            className="w-full mt-6 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
        </div>
      </div>
    );
  };

  // Modal de visualisation de ticket - UNIFIÉ avec TableauDeBordPage
  const TicketViewModal = () => {
    const navigate = useNavigate();
    
    if (!selectedTicket) return null;

    const getSubTypeLabel = (subType) => {
      const labels = {
        'soins': 'Soins',
        'marechalerie': 'Maréchalerie',
        'dentisterie': 'Dentisterie',
        'infrastructure': 'Infrastructure',
        'eau_alimentation': 'Eau & Alimentation',
        'equipements': 'Équipements',
        'terrain': 'Terrain',
        'autres': 'Autres'
      };
      return labels[subType] || subType;
    };

    const handleLocateOnMap = async () => {
      try {
        if (selectedTicket.type === 'medical') {
          if (selectedTicket.horseName) {
            const animauxQuery = query(
              collection(db, 'animaux'),
              where('nom', '==', selectedTicket.horseName)
            );
            const animauxSnapshot = await getDocs(animauxQuery);
            
            if (!animauxSnapshot.empty) {
              const animalDoc = animauxSnapshot.docs[0];
              const animalId = animalDoc.id;
              const animalData = animalDoc.data();
              
              if (animalData.zoneId) {
                navigate('/carte', { 
                  state: { 
                    focusAnimalId: animalId,
                    ticketInfo: {
                      type: selectedTicket.type,
                      title: selectedTicket.title,
                      message: selectedTicket.message || selectedTicket.content
                    }
                  } 
                });
              } else {
                alert(`L'animal "${selectedTicket.horseName}" n'est assigné à aucune zone sur la carte.`);
              }
            } else {
              alert(`Animal "${selectedTicket.horseName}" non trouvé dans la base de données`);
            }
          }
        } 
        else if (selectedTicket.type === 'technique') {
          if (selectedTicket.gpsLocation) {
            navigate('/carte', { 
              state: { 
                focusTechnicalGPS: {
                  lat: selectedTicket.gpsLocation.latitude,
                  lng: selectedTicket.gpsLocation.longitude
                },
                ticketInfo: {
                  type: selectedTicket.type,
                  title: selectedTicket.title,
                  message: selectedTicket.message || selectedTicket.content,
                  priority: selectedTicket.priority
                }
              } 
            });
          } 
          else if (selectedTicket.zoneId || selectedTicket.zoneName) {
            navigate('/carte', { 
              state: { 
                focusZoneName: selectedTicket.zoneName,
                ticketInfo: {
                  type: selectedTicket.type,
                  title: selectedTicket.title,
                  message: selectedTicket.message || selectedTicket.content,
                  priority: selectedTicket.priority
                }
              } 
            });
          } else {
            alert('Ce ticket technique n\'a pas de localisation définie');
          }
        }
      } catch (error) {
        console.error('Erreur lors de la localisation:', error);
        alert('Erreur lors de la localisation sur la carte : ' + error.message);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Détails du ticket</h2>
            <button
              onClick={() => setSelectedTicket(null)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getIcon(selectedTicket.type)}
                <span className="font-medium capitalize">{selectedTicket.type}</span>
                {selectedTicket.subType && (
                  <span className="text-sm text-gray-600">
                    - {getSubTypeLabel(selectedTicket.subType)}
                  </span>
                )}
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                selectedTicket.priority === 'haute' ? 'bg-red-100 text-red-800' :
                selectedTicket.priority === 'moyenne' ? 'bg-orange-100 text-orange-800' :
                'bg-green-100 text-green-800'
              }`}>
                {selectedTicket.priority}
              </span>
            </div>

            {selectedTicket.type === 'medical' && selectedTicket.horseName && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {selectedTicket.animalType === 'equide' || (!selectedTicket.animalType && selectedTicket.horseSire) ? (
                      <GiHorseHead className="h-5 w-5 text-gray-600" />
                    ) : (
                      <PiFarm className="h-5 w-5 text-gray-600" />
                    )}
                    <div>
                      <span className="text-sm text-gray-600">Animal: </span>
                      <span className="font-semibold text-gray-900">{selectedTicket.horseName}</span>
                      {selectedTicket.horseSire && (
                        <span className="text-xs text-gray-500 ml-2">(SIRE: {selectedTicket.horseSire})</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleLocateOnMap}
                    className="p-2 bg-blue-100 hover:bg-blue-200 rounded-full transition-colors"
                    title="Localiser sur la carte"
                  >
                    <MdWhereToVote className="h-5 w-5 text-blue-600" />
                  </button>
                </div>
              </div>
            )}

            {selectedTicket.type === 'technique' && selectedTicket.zoneName && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-600">Zone: </span>
                    <span className="font-semibold text-gray-900">{selectedTicket.zoneName}</span>
                  </div>
                  <button
                    onClick={() => {
                      navigate('/tableau-bord', { 
                        state: { 
                          zoneId: selectedTicket.zoneId,
                          zoneName: selectedTicket.zoneName,
                          filter: 'technique'
                        }
                      });
                      setSelectedTicket(null);
                    }}
                    className="text-sm px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                  >
                    Voir tous les tickets de cette zone
                  </button>
                </div>
              </div>
            )}

            <div>
              <h3 className="font-semibold text-lg">
                {selectedTicket.title || `Ticket ${selectedTicket.type}`}
              </h3>
            </div>

            <div>
              <h4 className="font-medium text-gray-700 mb-1">Description:</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                {selectedTicket.message || selectedTicket.content}
              </p>
            </div>

            {selectedTicket.type === 'technique' && (
              <div className="flex justify-center">
                <button
                  onClick={handleLocateOnMap}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                >
                  <MdWhereToVote className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">Localiser l'intervention</span>
                </button>
              </div>
            )}

            <div className="border-t pt-3 space-y-1 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3" />
                <span>Créé le {formatDateTime(selectedTicket.dateCreation)}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-3 w-3" />
                <span>Par {selectedTicket.acteur}</span>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={() => setSelectedTicket(null)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Fermer
              </button>
              {selectedTicket.type === 'medical' && selectedTicket.horseId && (
                <button
                  onClick={() => navigate(`/animaux/${selectedTicket.horseId}`)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Voir Fiche
                </button>
              )}
              <button
                onClick={() => {
                  handleCloseTicket(selectedTicket.id);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Clôturer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header compact avec indicateurs de filtrage */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between max-w-[1920px] mx-auto">
          <div className="flex items-center gap-4">
            <img 
              src="/logo.svg" 
              alt="HIPOO" 
              className="h-8 w-auto"
            />
            <h1 className="text-xl font-bold">Pilotage - Vue Liste</h1>
            {/* Indicateurs de filtrage */}
            {filterZoneName && filterType === 'technique' && (
              <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 text-sm rounded">
                Zone: {filterZoneName}
              </span>
            )}
            {filterHorseId && filterType === 'medical' && (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                Filtré par animal
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Bouton pour réinitialiser le filtre si actif */}
            {(filterZoneId || filterHorseId) && (
              <button
                onClick={() => {
                  navigate('/tableau-bord-liste');
                  window.location.reload();
                }}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 underline"
              >
                Voir tous les tickets
              </button>
            )}
            <button
              onClick={() => setShowNewTicketModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-black text-white text-sm rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Créer un ticket
            </button>
            <button
              onClick={() => navigate('/tableau-bord')}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors"
            >
              <LuLayoutDashboard className="h-4 w-4" />
              Vue Classique
            </button>
          </div>
        </div>
      </div>

      {/* Contenu principal - 4 blocs */}
      <div className="p-4 max-w-[1920px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          
          {/* Bloc Tickets Médicaux */}
          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg overflow-hidden">
            <div className="bg-blue-100 p-3 border-b border-blue-300 flex items-center gap-2">
              <FaHandHoldingMedical className="h-5 w-5 text-blue-700" />
              <h2 className="font-bold text-blue-900">Tickets Médicaux ({medicalTickets.length})</h2>
            </div>
            <div className="p-2 max-h-[400px] overflow-y-auto">
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin h-6 w-6 border-3 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : medicalTickets.length > 0 ? (
                <table className="w-full text-xs">
                  <tbody>
                    {medicalTickets.map((ticket) => (
                      <tr 
                        key={ticket.id}
                        className="hover:bg-blue-100 cursor-pointer border-b border-blue-200"
                        onClick={() => setSelectedTicket(ticket)}
                      >
                        <td className="p-2">
                          <PriorityDot priority={ticket.priority} />
                        </td>
                        <td className="p-2 font-medium">
                          {ticket.horseName || '-'}
                        </td>
                        <td className="p-2">
                          {truncateText(ticket.title, 30)}
                        </td>
                        <td className="p-2 text-gray-600">
                          {truncateText(ticket.message || ticket.content, 40)}
                        </td>
                        <td className="p-2 text-gray-500">
                          {formatDateTime(ticket.dateCreation)}
                        </td>
                        <td className="p-2 text-gray-500">
                          {getTimeSinceCreation(ticket.dateCreation)}
                        </td>
                        <td className="p-2">
                          <button
                            onClick={(e) => handleCloseTicket(ticket.id, e)}
                            className="text-green-600 hover:text-green-800"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center py-4 text-gray-500">Aucun ticket médical</p>
              )}
            </div>
          </div>

          {/* Bloc Tickets Techniques */}
          <div className="bg-purple-50 border-2 border-purple-300 rounded-lg overflow-hidden">
            <div className="bg-purple-100 p-3 border-b border-purple-300 flex items-center gap-2">
              <FaWrench className="h-5 w-5 text-purple-700" />
              <h2 className="font-bold text-purple-900">Tickets Techniques ({technicalTickets.length})</h2>
            </div>
            <div className="p-2 max-h-[400px] overflow-y-auto">
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin h-6 w-6 border-3 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : technicalTickets.length > 0 ? (
                <table className="w-full text-xs">
                  <tbody>
                    {technicalTickets.map((ticket) => (
                      <tr 
                        key={ticket.id}
                        className="hover:bg-purple-100 cursor-pointer border-b border-purple-200"
                        onClick={() => setSelectedTicket(ticket)}
                      >
                        <td className="p-2">
                          <PriorityDot priority={ticket.priority} />
                        </td>
                        <td className="p-2 font-medium">
                          {ticket.subType ? getSubTypeLabel(ticket.subType) : '-'}
                        </td>
                        <td className="p-2">
                          {truncateText(ticket.title, 30)}
                        </td>
                        <td className="p-2 text-gray-600">
                          {truncateText(ticket.message || ticket.content, 40)}
                        </td>
                        <td className="p-2 text-gray-500">
                          {formatDateTime(ticket.dateCreation)}
                        </td>
                        <td className="p-2 text-gray-500">
                          {getTimeSinceCreation(ticket.dateCreation)}
                        </td>
                        <td className="p-2">
                          <button
                            onClick={(e) => handleCloseTicket(ticket.id, e)}
                            className="text-green-600 hover:text-green-800"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center py-4 text-gray-500">Aucun ticket technique</p>
              )}
            </div>
          </div>

          {/* Bloc Échéances */}
          <div className="bg-orange-50 border-2 border-orange-300 rounded-lg overflow-hidden">
            <div className="bg-orange-100 p-3 border-b border-orange-300 flex items-center gap-2">
              <LuBellRing className="h-5 w-5 text-orange-700" />
              <h2 className="font-bold text-orange-900">Échéances à venir</h2>
            </div>
            <div className="p-2 max-h-[400px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-orange-200">
                    <th className="p-2 text-left">Priorité</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-left">Animal</th>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Dans</th>
                  </tr>
                </thead>
                <tbody>
                  {mockDeadlines.map((deadline) => (
                    <tr key={deadline.id} className="hover:bg-orange-100 border-b border-orange-200">
                      <td className="p-2">
                        <PriorityDot priority={deadline.priority} />
                      </td>
                      <td className="p-2">{deadline.type}</td>
                      <td className="p-2 font-medium">{deadline.animal}</td>
                      <td className="p-2">{deadline.date}</td>
                      <td className="p-2 text-orange-700 font-medium">{deadline.daysLeft}j</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-center py-2 text-gray-400 text-xs italic mt-4">
                Module en cours de développement
              </div>
            </div>
          </div>

          {/* Bloc Statistiques */}
          <div className="bg-green-50 border-2 border-green-300 rounded-lg overflow-hidden">
            <div className="bg-green-100 p-3 border-b border-green-300 flex items-center gap-2">
              <MdOutlineQueryStats className="h-5 w-5 text-green-700" />
              <h2 className="font-bold text-green-900">Statistiques</h2>
            </div>
            <div className="p-2 max-h-[400px] overflow-y-auto">
              <table className="w-full text-xs">
                <tbody>
                  {mockStatsTable.map((stat, index) => (
                    <tr key={index} className="hover:bg-green-100 border-b border-green-200">
                      <td className="p-3 text-gray-700">{stat.label}</td>
                      <td className="p-3 text-right font-bold text-green-900 text-base">
                        {stat.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-center py-2 text-gray-400 text-xs italic mt-4">
                Données en temps réel
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Modals */}
      <TicketTypeSelectionModal />
      <TicketViewModal />
      <CloseConfirmModal />
      <TechnicalTicketModal
        isOpen={showTechnicalModal}
        onClose={() => setShowTechnicalModal(false)}
        fromDashboard={true}
        onSuccess={() => {
          setShowTechnicalModal(false);
        }}
      />
      <MedicalTicketModal
        isOpen={showMedicalModal}
        onClose={() => setShowMedicalModal(false)}
        horse={null}
        onSuccess={() => {
          setShowMedicalModal(false);
        }}
      />
    </div>
  );
}